/**
 * Cart Utilities - Venroy Theme
 * Centralized cart operations for add to cart functionality
 * Used across all quick add buttons (product grids, carousels, modals, search, etc.)
 */

/**
 * Add item to cart
 * @param {string|number} variantId - The variant ID to add
 * @param {Object} options - Configuration options
 * @param {number} options.quantity - Quantity to add (default: 1)
 * @param {Object} options.properties - Line item properties
 * @param {HTMLElement} options.errorContainer - Element to display errors
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Promise} Promise that resolves with cart data
 */
function addToCart(variantId, options = {}) {
  const {
    quantity = 1,
    properties = {},
    errorContainer = null,
    onSuccess = null,
    onError = null
  } = options;

  const formData = {
    items: [{
      id: variantId,
      quantity: quantity,
      ...(Object.keys(properties).length > 0 && { properties })
    }]
  };

  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(formData),
  })
  .then(response => response.json())
  .then(data => {
    if (data.status === 422) {
      // Handle out of stock or other errors
      if (errorContainer) {
        errorContainer.innerHTML = data.description;
        setTimeout(() => { errorContainer.innerHTML = ''; }, 2000);
      }
      if (onError) onError(data);
      throw new Error(data.description);
    }

    // Success - handle cart redirect and update count
    handleCartRedirect();
    updateCartCount();

    if (onSuccess) onSuccess(data);
    return data;
  })
  .catch(error => {
    console.error('Cart add error:', error);
    if (onError) onError(error);
    throw error;
  });
}

/**
 * Update cart count badge in header
 * Fetches current cart state and updates all .cart-count elements
 * @returns {Promise} Promise that resolves with cart data
 */
function updateCartCount() {
  return fetch('/cart.js')
    .then(response => response.json())
    .then(data => {
      if (window.SSBeacon) SSBeacon.cartUpdate(data);
      document.querySelectorAll('.cart-count').forEach(el => {
        el.innerHTML = data.item_count;
      });
      return data;
    })
    .catch(error => {
      console.error('Cart count update error:', error);
      throw error;
    });
}

/**
 * Open cart drawer with configurable delay
 * @param {number} delay - Delay in milliseconds before opening (default: 500)
 */
function openCartDrawer(delay = 500) {
  setTimeout(() => {
    document.querySelector('body').classList.add('open-drawer');
  }, delay);
}

/**
 * Handle cart redirect after successful add
 * If on cart page: reload the page
 * Otherwise: call load_cart() and open drawer, or redirect to cart
 */
function handleCartRedirect() {
  // If already on cart page, reload it
  if (document.location.href.indexOf('/cart') > -1) {
    window.location.href = "/cart";
    return;
  }

  // Otherwise, try to load cart drawer
  if (typeof load_cart === 'function') {
    load_cart();
    openCartDrawer(500);
  } else {
    // Fallback: redirect to cart page
    window.location.href = "/cart";
  }
}
